import { z } from 'zod';
import { getDashboardExternalPageExtensionsDir } from '@wix/cli-astro-definitions';
import {
  VALID_PATH_REGEX,
  pathAvailableValidation,
} from '../../../dev-center/components/validations/common.js';
import {
  EXTERNAL_PAGE_URL_ERROR,
  isValidExternalPageUrl,
  normalizeExternalPageUrl,
} from '../../../dev-center/components/validations/dashboard-external-page.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { generateDashboardExternalPage } from './generate.js';

const schema = z.object({
  extensionType: z.literal('DASHBOARD_EXTERNAL_PAGE'),
  url: z
    .string()
    .refine(isValidExternalPageUrl, { message: EXTERNAL_PAGE_URL_ERROR })
    .transform(normalizeExternalPageUrl)
    .describe(
      defineField({
        title: 'External URL',
        description: 'The URL of the external page to load',
      })
    ),
  folder: z
    .string({ required_error: `'folder' must be specified` })
    .regex(VALID_PATH_REGEX)
    .describe(
      defineField({
        title: 'Page folder',
        description: 'Lowercase alphanumeric, hyphens, and slashes.',
      })
    ),
});

export const dashboardExternalPage: ExtensionDef<typeof schema> = {
  title: 'Dashboard External Page',
  description: 'A Dashboard page that loads an external URL',
  group: 'Dashboard',
  documentation: 'https://dev.wix.com/docs/wix-cli',
  zodSchema: schema,
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getDashboardExternalPageExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : `The page folder '${data.folder}' already exists. Choose a folder that has not been used yet in your application.`;
  },
  generate: ({ projectFolder, data }) =>
    generateDashboardExternalPage(projectFolder, data.folder, data.url),
};
